import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Lock, KeyRound, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PrivateAccessGate — a premium full-screen access-code entry for invitation-only tours.
 * Renders in place of the regular TourDetailPage when tour.is_private && !verified.
 *
 * Design: black / white / blue, mobile-first, no "demo/beta" language anywhere.
 */
const PrivateAccessGate = ({ tour, onAccessGranted, onBack }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input on mount so iOS keyboard opens immediately
    const id = setTimeout(() => inputRef.current?.focus(), 250);
    return () => clearTimeout(id);
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter your access code.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/tours/${tour.tour_id}/verify-access`, { access_code: trimmed });
      toast.success('Access granted — welcome.');
      onAccessGranted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Incorrect access code. Please try again.';
      setError(msg);
      try { inputRef.current?.select(); } catch (_) { /* noop */ }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white flex flex-col"
      data-testid="private-access-gate"
    >
      {/* Subtle ambient blue glow */}
      <div className="absolute top-0 left-0 right-0 h-[60vh] pointer-events-none bg-gradient-to-b from-blue-600/15 to-transparent" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none bg-blue-600/8 rounded-full blur-[160px]" />

      <header className="relative z-10 px-5 py-5">
        <button
          type="button"
          onClick={onBack}
          data-testid="gate-back-btn"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition text-sm font-bold uppercase tracking-wider"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tours
        </button>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md mx-auto">
          {/* Cover image (if any) */}
          {tour.cover_image_url && (
            <div className="mb-6 rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src={tour.cover_image_url}
                alt={tour.title}
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          )}

          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-[11px] font-bold uppercase tracking-[0.18em]">
            <Sparkles className="w-3.5 h-3.5" />
            Private Access
          </div>

          <h1 className="font-black text-3xl sm:text-4xl leading-tight tracking-tight mb-2">
            {tour.title || 'Invitation Only'}
          </h1>
          {tour.city && (
            <p className="text-white/60 text-sm mb-8">
              {tour.city}
            </p>
          )}

          <div className="mb-6">
            <h2 className="font-black text-lg mb-1">Access Code Required</h2>
            <p className="text-white/70 text-sm leading-relaxed">
              This experience is invitation-only. Enter the access code from your invitation to continue.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); if (error) setError(''); }}
                placeholder="Enter access code"
                data-testid="access-code-input"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/15 focus:bg-white/10 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition placeholder:text-white/30 text-lg font-mono tracking-[0.2em] uppercase"
              />
            </div>

            {error && (
              <div
                className="px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-sm font-medium"
                data-testid="access-code-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="access-code-submit"
              className="w-full py-4 rounded-2xl bg-white text-slate-950 font-black uppercase tracking-wider shadow-2xl shadow-blue-500/20 active:scale-[0.99] hover:bg-blue-50 transition disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Lock className="w-4 h-4 animate-pulse" /> Verifying…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" /> Unlock Experience
                </>
              )}
            </button>
          </form>

          <p className="text-white/40 text-xs mt-6 text-center leading-relaxed">
            For invited stakeholders, partners, and VIP guests.
            <br />
            Need access? Contact your Audiostride host.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivateAccessGate;
